import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import Card from 'ui-library/Card';
import {GRAY_SEMIDARK, NEAR_BLACK} from 'ui-library/Colors';
import {Header3} from 'ui-library/Headers';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface ActionCardProps {
  icon: BenchlingIconName;
  text: string;
  appendInputText: (message: string) => void;
}

function ActionCard({icon, text, appendInputText}: ActionCardProps): JSX.Element {
  return (
    <Card
      onSelect={() => {
        appendInputText(text);
      }}
    >
      <div className={css(styles.cardContent)}>
        <div className={css(styles.iconContainer)}>
          <BenchlingIcon icon={icon} />
        </div>
        <span className={css(styles.text)}>{text}</span>
      </div>
    </Card>
  );
}

export interface DeepResearchEmptyStateProps {
  appendInputText: (message: string) => void;
}

export function DeepResearchEmptyState({appendInputText}: DeepResearchEmptyStateProps): JSX.Element {
  return (
    <div className={css(styles.container)}>
      <Header3 className={css(styles.welcomeHeading)}>Welcome!</Header3>
      <span className={css(styles.description)}>
        First, ask a detailed prompt. It may take some time to generate your response. You'll receive a
        one-time, in-depth report to copy into Benchling. After that, you can start a new prompt.
      </span>

      <div className={css(styles.actionsContainer)}>
        <ActionCard icon="pen-to-square" text="Write a report about" appendInputText={appendInputText} />
        <ActionCard icon="lightbulb" text="Analyze insights across" appendInputText={appendInputText} />
        <ActionCard icon="book-open-cover" text="Design and plan for" appendInputText={appendInputText} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    textAlign: 'center',
    gap: '18px',
    maxWidth: '500px',
  },
  welcomeHeading: {
    fontSize: '24px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: NEAR_BLACK,
  },

  description: {
    ...FONT_PROXIMA_13,
    color: GRAY_SEMIDARK,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },

  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
    maxWidth: '400px',
  },

  // Card content styles
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    width: '100%',
    gap: '16px',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    fontSize: '20px',
    color: GRAY_SEMIDARK,
  },
  text: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: NEAR_BLACK,
  },
});
